import React, { useEffect, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import { BirdiTheme, breakpoints } from '@birdi/theme';
import { useNavigate } from 'react-router';
import { ai as aiAPI, maps as mapsAPI, stroot } from '@birdi/js-sdk';
import type { AiDetection } from '@birdi/js-sdk/src/public/ai';
import { useMetaSelector } from '../../meta/reducer';
import { posthog } from '../../lib/posthog';
import type { WidgetProps } from '../tabs';
import { ReorderingIcon } from './reordering-icon';
import MapSVG from '@birdi/icons/map.svg';
import ImageSVG from '@birdi/icons/image.svg';

const DETECTIONS_PER_PAGE = 3;

const formatDate = (dateString: string) => {
  try {
    // Use UTC to avoid timezone shifts (e.g., showing 22 Jan instead of 21 Jan)
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });
  } catch {
    return '-';
  }
};

export const LatestAIDetections: React.FC<WidgetProps> = ({
  isReorderMode,
  isHovered,
  isDragging,
  suppressIntercomAnchor,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const meta = useMetaSelector();

  const [detections, setDetections] = useState<AiDetection[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const workspaceId = meta.workspace?.id || meta.organisation.id;
  const canCreateMaps = meta.permissions.includes('workspace:map:create');

  const onNewMap = async () => {
    const res = (await mapsAPI.postMap(stroot('nwmap4')))?.body;
    if (res && res.id) {
      navigate(`/map/next/${res.id}`);
    }
  };

  const fetchDetections = async (limit: number) => {
    if (!meta.organisation?.id || !workspaceId) {
      return { detections: [], hasMore: false };
    }
    // Fetch one extra to check if there are more
    const res = await aiAPI.getOrganisationDetections(
      stroot('aidtdsh'),
      workspaceId,
      { limit: limit + 1 },
    );
    const fetchedDetections = res.body || [];
    const hasMoreItems = fetchedDetections.length > limit;
    return {
      detections: hasMoreItems
        ? fetchedDetections.slice(0, limit)
        : fetchedDetections,
      hasMore: hasMoreItems,
    };
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const result = await fetchDetections(DETECTIONS_PER_PAGE);
        setDetections(result.detections);
        setHasMore(result.hasMore);
      } catch (e) {
        console.error('Error fetching AI detections:', e);
        setDetections([]);
        setHasMore(false);
      }
      setLoading(false);
    })();
  }, [meta.organisation?.id, workspaceId]);

  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    posthog?.capture('dashboard latest ai detections expand clicked');

    try {
      const newLimit = detections.length + DETECTIONS_PER_PAGE;
      const result = await fetchDetections(newLimit);
      setDetections(result.detections);
      setHasMore(result.hasMore);
    } catch (e) {
      console.error('Error loading more AI detections:', e);
    }
    setLoadingMore(false);
  };

  const isExpandDisabled =
    detections.length === 0 || !hasMore || loading || loadingMore;

  const handleDetectionClick = (detection: AiDetection) => {
    posthog?.capture('dashboard latest ai detection clicked', {
      detectionId: detection.id,
      mapId: detection.mapId,
      method: detection.method,
    });
    // Navigate to the map
    if (detection.mapId) {
      navigate(`/map/next/${detection.mapId}`);
    }
  };

  const getCreatorName = (detection: AiDetection) => {
    if (detection.creatorFname && detection.creatorLname) {
      return `${detection.creatorFname} ${detection.creatorLname}`;
    }
    return 'Unknown user';
  };

  // Row style for consistent layout
  const rowStyle = css`
    display: flex;
    margin-bottom: 0.15em;
  `;

  const labelStyle = (theme: BirdiTheme) => css`
    font-weight: 600;
    color: ${theme.midnight};
    min-width: 100px;
  `;

  const valueStyle = (theme: BirdiTheme) => css`
    color: ${theme.midnight};
    margin-left: 0.5em;
  `;

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        border-radius: 1em;
        box-shadow: 2px 2px 2px ${theme.mono30};
        background: ${theme.mono0};
        min-height: 320px;
        min-width: 0;
        max-width: 100%;
        @media (max-width: ${breakpoints.s}) {
          min-height: 220px;
        }
        @media (max-width: ${breakpoints.xs}) {
          min-height: 180px;
          width: 100%;
        }
      `}
    >
      <div
        css={css`
          color: ${theme.midnight};
          font-weight: 700;
          background: ${theme.lightOcean};
          padding: 1em;
          border-top-left-radius: 1em;
          border-top-right-radius: 1em;
          display: flex;
          align-items: center;
          gap: 0.5em;
        `}
        id={suppressIntercomAnchor ? undefined : 'latest-ai-detections'}
      >
        <ReorderingIcon
          isReorderMode={isReorderMode}
          isHovered={isHovered}
          isDragging={isDragging}
        />
        <span>Latest AI detections</span>
      </div>
      <div
        css={css`
          flex: 1 1 auto;
          padding: 1em;
          color: ${theme.mono50};
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
        `}
      >
        <div
          css={css`
            margin-bottom: 1em;
          `}
        >
          See what AI is finding across your maps and images.
        </div>

        {/* Empty state */}
        {!loading && detections.length === 0 && (
          <div
            css={css`
              height: 13em;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: flex-start;
            `}
          >
            <div>
              <strong>Your AI detections will appear here.</strong>
            </div>
            <div
              css={css`
                margin-top: 0.5em;
              `}
            >
              Run AI detection on your latest imagery or map to start seeing
              results in this space.
            </div>
            {canCreateMaps && (
              <button
                onClick={onNewMap}
                css={css`
                  color: ${theme.deepOcean};
                  text-decoration: underline;
                  background: none;
                  border: none;
                  padding: 0;
                  font: inherit;
                  cursor: pointer;
                  margin-top: 0.5em;
                `}
              >
                + create a map
              </button>
            )}
          </div>
        )}

        {/* Detections list */}
        {!loading && detections.length > 0 && (
          <div>
            {detections.map((detection) => (
              <div
                key={detection.id}
                onClick={() => handleDetectionClick(detection)}
                css={css`
                  display: flex;
                  background: ${theme.mono0};
                  border-radius: 8px;
                  border: 1px solid ${theme.mono30};
                  margin-bottom: 0.5em;
                  padding: 1em;
                  gap: 1em;
                  cursor: pointer;
                  transition:
                    box-shadow 0.15s,
                    background 0.15s;
                  &:hover {
                    // box-shadow: 0 3px 12px ${theme.mono30};
                    background: ${theme.mono20};
                    border: none;
                  }
                `}
              >
                {/* Thumbnail */}
                <div
                  css={css`
                    width: 11em;
                    min-width: 11em;
                    height: 9em;
                    border-radius: 5px;
                    background: ${theme.sand};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    overflow: hidden;
                  `}
                >
                  {detection.thumbnailUrl ? (
                    <img
                      src={detection.thumbnailUrl}
                      alt={detection.prompt || 'Detection thumbnail'}
                      css={css`
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                      `}
                    />
                  ) : (
                    <ImageSVG />
                  )}
                </div>

                {/* Detection info */}
                <div
                  css={css`
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                  `}
                >
                  {/* Detection */}
                  <div css={rowStyle}>
                    <span css={labelStyle(theme)}>Detection:</span>
                    <span css={valueStyle(theme)}>
                      {detection.prompt || 'Unknown'}
                    </span>
                  </div>

                  {/* Annotations */}
                  <div css={rowStyle}>
                    <span css={labelStyle(theme)}>Annotations:</span>
                    <span css={valueStyle(theme)}>
                      {detection.numOutputs || '0'}
                    </span>
                  </div>

                  {/* Date */}
                  <div css={rowStyle}>
                    <span css={labelStyle(theme)}>Date:</span>
                    <span css={valueStyle(theme)}>
                      {detection.createdAt
                        ? formatDate(detection.createdAt)
                        : '-'}
                    </span>
                  </div>

                  {/* User */}
                  <div css={rowStyle}>
                    <span css={labelStyle(theme)}>User:</span>
                    <span css={valueStyle(theme)}>
                      {getCreatorName(detection)}
                    </span>
                  </div>

                  {/* Map name - gray color */}
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: 0.5em;
                      color: ${theme.mono50};
                      margin-top: 0.25em;
                    `}
                  >
                    <span
                      css={css`
                        display: flex;
                        width: 14px;
                        height: 14px;
                      `}
                    >
                      <MapSVG />
                    </span>
                    <span>{detection.mapName || 'Untitled Map'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {loading && (
          <div
            css={css`
              text-align: center;
              margin: 1em 0;
            `}
          >
            Loading...
          </div>
        )}
      </div>

      <div
        css={css`
          padding: 1em;
        `}
      >
        <button
          onClick={handleLoadMore}
          disabled={isExpandDisabled}
          css={css`
            width: 100%;
            color: ${isExpandDisabled ? theme.mono40 : theme.mono60};
            border: solid 1px ${theme.mono30};
            border-radius: 5px;
            padding: 0.5em;
            font-weight: 500;
            cursor: ${isExpandDisabled ? 'not-allowed' : 'pointer'};
            background: ${theme.mono0};
            opacity: ${isExpandDisabled ? 0.6 : 1};
          `}
        >
          {loadingMore ? 'Loading...' : 'Expand'}
        </button>
      </div>
    </div>
  );
};
